import React from "react";

/**
 * Reusable Loading component.
 * @param {boolean} show - Whether to show the loader
 * @param {string} [message] - Primary message (e.g. "Processing your file...")
 * @param {string} [subMessage] - Secondary message (e.g. "Please wait while we import your data")
 * @param {React.ReactNode} [icon] - Optional icon to show inside the spinner
 * @param {boolean} [overlay] - If true, renders as overlay with backdrop; if false, inline block
 * @param {"absolute"|"fixed"} [overlayMode] - Overlay positioning (default absolute)
 * @param {string} [className] - Extra classes for the overlay container
 */
const Loading = ({
  show,
  message = "Loading...",
  subMessage,
  icon,
  overlay = true,
  overlayMode = "absolute",
  className = "",
}) => {
  if (!show) return null;

  const content = (
    <div className="flex flex-col items-center space-y-4 px-4">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-[var(--accent-soft,#fbe4e4)] border-t-[var(--accent,#a51c1c)] rounded-full animate-spin" />
        {icon && (
          <div className="absolute inset-0 flex items-center justify-center">
            {icon}
          </div>
        )}
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-[var(--ink,#1c1c1a)]">{message}</p>
        {subMessage && (
          <p className="text-xs text-[var(--muted,#6b6a66)] mt-1">{subMessage}</p>
        )}
      </div>
    </div>
  );

  if (overlay) {
    const positionClass =
      overlayMode === "fixed" ? "fixed inset-0" : "absolute inset-0";
    return (
      <div
        className={`${positionClass} bg-white/90 backdrop-blur-sm z-[100] flex flex-col items-center justify-center ${className}`}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        {content}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-4">{content}</div>
  );
};

export default Loading;
